/**
 * schermate.js — UNA tabella per le rotte, il menu e la barra comandi.
 *
 * PERCHE' ESISTE (stile «Console», fase 1, Alex 2/10/2026). Fino a qui
 * gli elenchi erano due: le rotte in App.js e le voci in Layout.jsx, con
 * i ruoli scritti a mano in tutti e due e nessuno che li confrontasse.
 * Una voce poteva comparire a un ruolo che la rotta rimanda indietro (un
 * clic che porta alla dashboard insegna a diffidare del menu), o mancare
 * a un ruolo che la rotta ammette (una funzione che quel ruolo non sa di
 * avere). Adesso:
 *   - App.js costruisce le rotte da SCHERMATE, e la guardia di ogni rotta
 *     e' esitoGuardia();
 *   - il menu (vociMenu), il «vai a» di Ctrl K (vociVaiA), le azioni del
 *     ruolo (azioniDi) e le linguette (linguetteDi) leggono la STESSA
 *     tabella e filtrano con la STESSA funzione (siApre), che chiede a
 *     esitoGuardia. Il menu non puo' offrire una schermata che la rotta
 *     non apre, perche' a deciderlo e' la stessa riga di codice.
 *
 * NON E' PROTEZIONE. Il server controlla ogni rotta per conto suo, ed e'
 * l'unico controllo vero: qui si allinea cio' che si mostra.
 *
 * GLI INDIRIZZI NON SI CAMBIANO: ci sono i segnalibri della clinica e le
 * prove dei percorsi. Una schermata che cambia nome cambia `etichetta`,
 * non `percorso`.
 *
 * LA FORMA DI UNA RIGA
 *   percorso     l'indirizzo, come in react-router («:id» per i dettagli)
 *   Componente   la pagina (import qui sopra: un nome sbagliato lo ferma
 *                verificaNomi prima della build); `props` se ne vuole
 *   guardia      'pubblica'       nessun controllo (login, tablet...)
 *                'protetta'       chiunque abbia un account del gestionale
 *                'admin'          solo l'amministrazione
 *                'ruoli'          solo i `ruoli` elencati
 *                'acquisizione'   la pagina del telefono, fuori dal guscio
 *                'cambioPassword' solo chi deve cambiare la password
 *                'rimando'        porta a `rimanda`
 *                Le prime tre di «protetta/admin/ruoli» stanno nel guscio
 *                (menu e barra); le altre no.
 *   attesa       testo mentre si carica una pagina caricata a parte
 *   gruppo       'lavoro' | 'clinica' | 'amministrazione' | 'sistema' (GRUPPI)
 *   etichetta    il nome nel menu, nel percorso in alto e in Ctrl K
 *   Icona        l'icona del menu (lucide)
 *   menu         true: e' una voce del menu
 *   vaiA         true: Ctrl K la offre nel «vai a» (ogni voce del menu lo e')
 *   cerca        altri nomi con cui la si cerca in Ctrl K: i nomi di prima,
 *                cosi' chi cerca «Fatturazione» trova «Fatture»
 *   genitore     per una schermata di dettaglio: la schermata da cui si
 *                arriva (il percorso in alto, la voce accesa nel menu)
 *   linguettaDi  e' una linguetta di un'altra schermata; `linguetta` e'
 *                l'etichetta della linguetta (anche sulla schermata madre)
 *   contatore    il codice del numero accanto alla voce, da GET
 *                /api/contatori (useContatori): solo i quattro di CONTATORI
 *   contatoreApre  il numero e' un collegamento suo, accanto alla voce e non
 *                dentro: { indirizzo, descrivi }. `indirizzo` e' il percorso
 *                della riga con una ricerca (?...), quindi la stessa guardia
 *                della voce; `descrivi(n)` e' il nome del numero per il
 *                lettore di schermo e il suggerimento del mouse. Senza, il
 *                numero sta dentro la voce e il clic porta alla voce. Oggi
 *                solo Pazienti (Alex, 6/10/2026, risposta 22)
 *   azioni       le azioni di creazione della schermata, per Ctrl K e per
 *                «Nuovo»: { codice, etichetta, ruoli? }. Si aprono andando
 *                a `percorso` con lo stato { azione: codice }: la pagina che
 *                vuole aprire subito il suo modulo legge location.state.
 *                `ruoli` solo se il server ammette a crearla meno ruoli di
 *                quanti ne ammette a vederla.
 *   stile        'classico' | 'console': la schermata c'e' solo con quello
 *                stile (interruttore dello stile, Alex 14/10/2026). Con
 *                l'altro la rotta rimanda alla pagina iniziale dell'utente:
 *                /dashboard e' dello stile di prima, /oggi dello stile nuovo.
 *                Senza `stile` la schermata c'e' con tutti e due.
 */
import React from 'react';
import {
    LayoutDashboard, CalendarDays, Users, ListChecks, UserCheck, Clock,
    ClipboardList, ListTree, FileCheck2, FileSignature, BookOpen, Radiation,
    ReceiptText, Building2, Landmark, Wallet, Boxes, Package, Syringe,
    Stethoscope, LineChart, PieChart, Activity, UserCog, FileSearch, History,
    ShieldAlert, Trash2, CalendarClock, DoorOpen, Tablet as IconaTablet,
    BadgeCheck, Scale, Sparkles, PackageOpen, BellRing, FileText, Settings,
    UserRound,
} from 'lucide-react';
import { FRONT_DESK } from '../utils/permessi';
/* Le guardie mandano alla pagina iniziale dell'utente diritte, senza
   passare da un rimando. La pagina iniziale dipende dallo stile (Alex,
   14/10/2026): /dashboard con quello di prima, /oggi con quello nuovo. */
import { paginaIniziale, stileNuovo } from './paginaIniziale';
import { ELENCO_DA_COMPLETARE, descriviNumeroDaCompletare } from '../utils/anagraficheDaCompletare';
import Login from '../pages/Login';
import CambiaPassword from '../pages/CambiaPassword';
import Profilo from '../pages/Profilo';
import PasswordDimenticata from '../pages/PasswordDimenticata';
import ReimpostaPassword from '../pages/ReimpostaPassword';
/* La Dashboard e il guscio di prima (interruttore dello stile, Alex
   14/10/2026): a stile spento il gestionale e' quello del 638a8209. Stanno
   QUI, dopo ReimpostaPassword e prima delle altre pagine, perche' i fogli di
   stile entrano nel pacchetto nell'ordine degli import: e' l'ordine che
   avevano in App.js il 638a8209 (Dashboard.css, poi i fogli del Layout di
   allora, poi le pagine), e con lo stesso ordine vince la stessa regola.
   LayoutClassico non e' una pagina: lo usa App.js, che lo importa dopo
   questa tabella. */
import Dashboard from '../pages/Dashboard';
import LayoutClassico from '../components/LayoutClassico';
/* «Oggi» e' la pagina iniziale dello stile nuovo (proposta 26, fase 2). */
import Oggi from '../pages/Oggi';
import PazientiElenco from '../components/pazienti/PazientiElenco';
import PazienteDetail from '../components/pazienti/PazienteDetailPagina';
import MediciList from '../components/medici/MediciList';
import PrestazioniList from '../components/prestazioni/PrestazioniList';
import PacchettiSpeciali from '../pages/PacchettiSpeciali';
import ConsensiDaReinformare from '../pages/ConsensiDaReinformare';
import ModelliConsenso from '../pages/ModelliConsenso';
import Spettanze from '../components/Spettanze';
import GestioneUtenti from '../pages/GestioneUtenti';
import Cestino from '../pages/Cestino';
import Promemoria from '../pages/Promemoria';
import Magazzino from '../pages/Magazzino';
import AreaDipendenti from '../pages/AreaDipendenti';
import AccettazioniTablet from '../pages/AccettazioniTablet';
import ApertureRiservate from '../pages/ApertureRiservate';
import StatoSistema from '../pages/StatoSistema';
import DatiFiscali from '../pages/DatiFiscali';
import SoggettiFatturazione from '../pages/SoggettiFatturazione';
import AmministrazioneAi from '../pages/AmministrazioneAi';
import Amministrazione from '../pages/Amministrazione';
import CatalogoCampi from '../pages/CatalogoCampi';
import SchedeCliniche from '../pages/SchedeCliniche';
import ValidazioneCampi from '../pages/ValidazioneCampi';
import PannelloAnalisi from '../pages/PannelloAnalisi';
import Acquisizione from '../pages/Acquisizione';
import Dispositivi from '../pages/Dispositivi';
import TestiRiutilizzabili from '../pages/TestiRiutilizzabili';
import AlbiSedi from '../pages/AlbiSedi';
import MieSchede from '../pages/MieSchede';
import MioContoProdotti from '../pages/MioContoProdotti';
import RegistroRxCentrale from '../pages/RegistroRxCentrale';
import Agenda from '../pages/AgendaPagina';
import GestioneOrari from '../pages/GestioneOrari';
import GestioneStanze from '../pages/GestioneStanze';
import ControlloGestione from '../pages/ControlloGestione';
import SistemaTS from '../pages/SistemaTS';
import Fatturazione from '../pages/FatturazionePagina';
import Compiti from '../pages/Compiti';
import Tablet from '../pages/Tablet';
import RegistroAccessi from '../pages/RegistroAccessi';
import RegistroModifiche from '../pages/RegistroModifiche';

/* Il referto si carica a parte, in un pezzo suo che contiene la sola
   pagina del referto: misurato il 3/10, 19,7 kB (6,45 kB compressi), e
   accanto il suo .css. L'editor NON sta li': TestiRiutilizzabili lo importa
   direttamente, quindi e' nel bundle principale, e nella build il lazy
   dell'editor in Referto.jsx diventa un Promise.resolve(). Se il pezzo non
   arriva, la «seconda difesa» (l'errorElement che App.js mette sulle righe
   con `attesa`).
   ATTENZIONE A UN PEZZO ANNIDATO (l'editor caricato davvero a parte, un
   altro lazy dentro il referto): il pezzo esterno arriva, quello dentro no.
   E' il motivo per cui il freno della seconda difesa e' legato alla pagina
   nata dalla ricarica e non si toglie all'arrivo del pezzo: toglierlo qui,
   nel .then del lazy, rifaceva il giro di ricariche (revisione del 3/10,
   utils/versioneInLinea.js). Lo prova il caso 18j dello scenario 15. */
const Referto = React.lazy(() => import('../pages/Referto'));

/** I quattro gruppi del menu, in quest'ordine (Alex, 2/10/2026). */
export const GRUPPI = [
    { codice: 'lavoro', etichetta: 'Lavoro' },
    { codice: 'clinica', etichetta: 'Clinica' },
    { codice: 'amministrazione', etichetta: 'Amministrazione' },
    { codice: 'sistema', etichetta: 'Sistema' },
];

/* I contatori ammessi (domanda 3, Alex 2/10/2026): solo cose da fare per
   chi guarda, e nessun contatore senza un controllo dietro. Li calcola il
   server, GET /api/contatori (backend/controllers/contatoriController.js);
   qui c'e' solo l'elenco dei nomi.
     compiti   i miei compiti aperti
     fatture   fatture da correggere e scartate (admin e segreteria)
     agenda    in sala d'attesa, finche' il medico non scrive nella visita:
               a un medico i suoi, ad admin e segreteria tutti (Alex,
               2/10/2026 sera e notte)
     pazienti  anagrafiche da completare di chi ha un appuntamento da oggi
               a sette giorni (admin e segreteria; Alex, 2/10/2026 sera) */
export const CONTATORI = ['compiti', 'fatture', 'agenda', 'pazienti'];

/* Chi puo' aprire la pagina del telefono: anche admin e segreteria, che
   hanno il telefono in mano come chiunque. */
const RUOLI_ACQUISIZIONE = ['acquisizione', 'admin', 'segreteria'];

/* I ruoli dei medici: le schermate solo per loro, e la vista di «Oggi»
   sul telefono (pages/Oggi.jsx), che e' solo per i medici (Alex,
   2/10/2026, domanda 6). */
export const MEDICI = ['medico', 'medico_senior'];

export const SCHERMATE = [
    /* ------------------------------------------------------------------
       Fuori dal guscio: niente menu, niente barra.
       ------------------------------------------------------------------ */
    { percorso: '/login', Componente: Login, guardia: 'pubblica' },
    /* Tablet in sala d'attesa: nessun utente collegato, si identifica con
       la chiave del dispositivo. Resta fuori dallo stile «Console». */
    { percorso: '/tablet', Componente: Tablet, guardia: 'pubblica' },
    /* Schermata di cambio password: senza Layout, cosi' non c'e' un menu
       da cui allontanarsi. Chi non deve cambiarla viene rimandato indietro. */
    { percorso: '/cambia-password', Componente: CambiaPassword, guardia: 'cambioPassword' },
    /* Pubbliche: chi ha perso la password non ha una sessione. */
    { percorso: '/password-dimenticata', Componente: PasswordDimenticata, guardia: 'pubblica' },
    { percorso: '/reimposta-password', Componente: ReimpostaPassword, guardia: 'pubblica' },
    /* Il ruolo «acquisizione» - il telefono dello studio - ha una pagina
       sola e non entra nel gestionale. */
    { percorso: '/acquisizione', Componente: Acquisizione, guardia: 'acquisizione' },

    /* ------------------------------------------------------------------
       Lavoro
       ------------------------------------------------------------------ */
    /* «Oggi» e' la pagina iniziale, per tutti i ruoli col contenuto per
       ruolo (Alex, 2/10/2026, domande 4 e 7): ha preso il posto della
       Dashboard, anche nel nome. Chi cerca «Dashboard» con Ctrl K la trova
       lo stesso. L'icona e' quella della Dashboard, come nel mockup. */
    { percorso: '/oggi', Componente: Oggi, guardia: 'protetta', stile: 'console',
      gruppo: 'lavoro', etichetta: 'Oggi', Icona: LayoutDashboard, menu: true, vaiA: true,
      cerca: ['Dashboard', 'Pagina iniziale', 'Oggi in ambulatorio'] },
    { percorso: '/agenda', Componente: Agenda, guardia: 'protetta',
      gruppo: 'lavoro', etichetta: 'Agenda', Icona: CalendarDays, menu: true, vaiA: true,
      contatore: 'agenda', cerca: ['Appuntamenti'],
      azioni: [{ codice: 'nuovo-appuntamento', etichetta: 'Nuovo appuntamento' }] },
    { percorso: '/pazienti', Componente: PazientiElenco, guardia: 'protetta',
      gruppo: 'lavoro', etichetta: 'Pazienti', Icona: Users, menu: true, vaiA: true,
      contatore: 'pazienti', cerca: ['Anagrafiche'],
      /* Il numero apre l'elenco gia' filtrato su «Da completare», cioe' le
         anagrafiche che conta (Alex, 6/10/2026, risposta 22); il clic sulla
         voce resta l'elenco intero. Lo stesso indirizzo che scrive il
         pulsante della pagina (utils/anagraficheDaCompletare.js). */
      contatoreApre: { indirizzo: ELENCO_DA_COMPLETARE, descrivi: descriviNumeroDaCompletare },
      /* L'accettazione e' l'unica strada per creare un paziente (passa dai
         moduli e dalle firme): le etichette sono quelle dei due pulsanti
         della pagina. Solo a segreteria e amministrazione, come i pulsanti
         (Alex, 12/10/2026, domanda 18: i medici non accettano pazienti;
         RUOLI_CREA_PAZIENTE in backend/services/anagraficaScrittura.js). */
      azioni: [{ codice: 'accettazione', etichetta: 'Accettazione paziente', ruoli: ['admin', 'segreteria'] },
               { codice: 'accettazione-prelievi', etichetta: 'Accettazione punto prelievi', ruoli: ['admin', 'segreteria'] }] },
    { percorso: '/pazienti/:id', Componente: PazienteDetail, guardia: 'protetta',
      gruppo: 'lavoro', etichetta: 'Cartella', genitore: '/pazienti' },
    { percorso: '/compiti', Componente: Compiti, guardia: 'protetta',
      gruppo: 'lavoro', etichetta: 'Compiti', Icona: ListChecks, menu: true, vaiA: true,
      linguetta: 'Compiti', contatore: 'compiti',
      azioni: [{ codice: 'nuovo-compito', etichetta: 'Nuovo compito' }] },
    /* Promemoria sta dentro Compiti, come linguetta (Alex, 2/10/2026): sono
       tutti e due «cose da fare», e il menu si accorcia di una voce. */
    { percorso: '/promemoria', Componente: Promemoria, guardia: 'protetta',
      gruppo: 'lavoro', etichetta: 'Promemoria', Icona: BellRing, vaiA: true,
      linguettaDi: '/compiti', linguetta: 'Promemoria' },
    /* Accettazioni e Presenze: lavoro del giorno (Alex, 2/10/2026). Le
       accettazioni sono del front-desk: la rotta e' riservata ad admin e
       segreteria, e la voce non compare ai medici, che altrimenti ci
       cliccherebbero per essere rimandati indietro. */
    { percorso: '/accettazioni', Componente: AccettazioniTablet, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'lavoro', etichetta: 'Accettazioni', Icona: UserCheck, menu: true, vaiA: true,
      cerca: ['Accettazioni dal tablet'] },
    /* Area dipendenti: la apre chiunque abbia un account, perche' ognuno ci
       trova le PROPRIE cose - per un'infermiera e' il posto dove trova le
       proprie ore e le proprie buste paga. Il confine fra «le mie» e
       «quelle di tutti» lo fa il server filtrando per identita', non questa
       riga filtrando per ruolo. Fino al 2/10 l'amministrazione la apriva
       dai riquadri di Amministrazione; adesso la voce e' la stessa per tutti. */
    { percorso: '/area-dipendenti', Componente: AreaDipendenti, guardia: 'protetta',
      gruppo: 'lavoro', etichetta: 'Presenze', Icona: Clock, menu: true, vaiA: true,
      cerca: ['Area dipendenti', 'Timbrature', 'Ferie', 'Buste paga'] },

    /* ------------------------------------------------------------------
       Clinica
       ------------------------------------------------------------------ */
    /* Le schede le compone l'amministrazione, ma la segreteria puo'
       attivarle, disattivarle e ordinarle: e' il server a separare le due
       cose. Per questo la voce c'e' anche per lei: un permesso che esiste
       sul server e non ha una strada per arrivarci non serve a nessuno. */
    { percorso: '/schede-cliniche', Componente: SchedeCliniche, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'clinica', etichetta: 'Schede cliniche', Icona: ClipboardList, menu: true, vaiA: true },
    /* La scheda che il medico compone per se'. Sta nel menu e non dentro il
       paziente perche' si prepara una volta e poi si usa, come il proprio
       listino: cercarla dentro una cartella vorrebbe dire aprirne una a
       caso per sistemare un campo. */
    { percorso: '/mie-schede', Componente: MieSchede, guardia: 'ruoli', ruoli: MEDICI,
      gruppo: 'clinica', etichetta: 'Le mie schede', Icona: ClipboardList, menu: true, vaiA: true },
    /* Il catalogo dei campi clinici lo cambia solo l'amministrazione. */
    { percorso: '/campi', Componente: CatalogoCampi, guardia: 'admin',
      gruppo: 'clinica', etichetta: 'Catalogo dei campi', Icona: ListTree, menu: true, vaiA: true,
      cerca: ['Campi clinici'] },
    /* La leggono tutti, medici compresi: un medico deve poter vedere che
       cosa gli e' stato chiesto di leggere. Scrivere e' un'altra cosa, e lo
       decidono le rotte del server. */
    { percorso: '/validazione-campi', Componente: ValidazioneCampi, guardia: 'protetta',
      gruppo: 'clinica', etichetta: 'Validazione dei campi', Icona: FileCheck2, menu: true, vaiA: true },
    /* I modelli di consenso. Aperta a tutti gli autenticati come le altre
       rotte dei modelli: un medico ci vede i propri e quelli della
       struttura, e chi apre un modello di un collega lo ferma il
       controller, non il routing. */
    { percorso: '/modelli-consenso', Componente: ModelliConsenso, guardia: 'protetta',
      gruppo: 'clinica', etichetta: 'Modelli di consenso', Icona: FileSignature, menu: true, vaiA: true,
      cerca: ['Consensi'],
      azioni: [{ codice: 'nuovo-modello', etichetta: 'Nuovo modello' }] },
    /* Cicli aperti su una versione di consenso precedente. Aperta a tutti
       gli autenticati: la segreteria richiama i pazienti e fissa le sedute,
       e sapere che a quello il medico deve dire una cosa prima di
       cominciare e' parte del suo lavoro. Annotare invece lo puo' solo un
       medico, e a impedirlo e' il server. Ci si arriva dai Modelli di
       consenso e dai documenti del paziente. */
    { percorso: '/consensi-da-reinformare', Componente: ConsensiDaReinformare, guardia: 'protetta',
      gruppo: 'clinica', etichetta: 'Consensi da reinformare', Icona: FileSignature, vaiA: true,
      genitore: '/modelli-consenso' },
    /* La libreria dei testi: frasi e protocolli, un testo scritto una volta
       e usato molte. La vedono tutti: il medico ci trova i suoi e quelli
       della struttura, l'amministrazione promuove; il server decide chi vede
       e scrive cosa. Testi e Ricette sono la stessa schermata: una voce sola
       (Alex, 2/10/2026), e le ricette sono la sua seconda linguetta. */
    { percorso: '/testi', Componente: TestiRiutilizzabili, guardia: 'protetta',
      gruppo: 'clinica', etichetta: 'Testi e ricette', Icona: BookOpen, menu: true, vaiA: true,
      linguetta: 'Testi riutilizzabili', cerca: ['Testi', 'Frasi', 'Protocolli'],
      azioni: [{ codice: 'nuovo-testo', etichetta: 'Nuovo testo' }] },
    /* Le ricette (Alex, 28/09/2026): la stessa libreria, solo il tipo
       «ricetta». */
    { percorso: '/ricette', Componente: TestiRiutilizzabili, props: { soloTipo: 'ricetta' }, guardia: 'protetta',
      gruppo: 'clinica', etichetta: 'Ricette', Icona: FileText, vaiA: true,
      linguettaDi: '/testi', linguetta: 'Ricette',
      azioni: [{ codice: 'nuova-ricetta', etichetta: 'Nuova ricetta' }] },
    /* Registro RX, elenco centrale. Solo front-desk: un medico le proprie
       righe le trova sulla scheda del paziente, e questa schermata esiste
       per il conto fra struttura e professionisti. */
    { percorso: '/registro-rx', Componente: RegistroRxCentrale, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'clinica', etichetta: 'Registro RX', Icona: Radiation, menu: true, vaiA: true },
    { percorso: '/referti/:id', Componente: Referto, attesa: 'Apertura del referto…', guardia: 'protetta',
      gruppo: 'clinica', etichetta: 'Referto' },

    /* ------------------------------------------------------------------
       Amministrazione
       ------------------------------------------------------------------ */
    /* Fatture, Aziende, Magazzino: lavoro del front-desk, che li apre
       decine di volte al giorno. Le rotte sono riservate ad admin e
       segreteria. */
    { percorso: '/fatturazione', Componente: Fatturazione, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'amministrazione', etichetta: 'Fatture', Icona: ReceiptText, menu: true, vaiA: true,
      contatore: 'fatture', cerca: ['Fatturazione', 'Cassa', 'Documenti'],
      azioni: [{ codice: 'nuovo-documento', etichetta: 'Nuovo documento' }] },
    /* Aziende accanto a Fatture (Alex, 2/10/2026): sono i soggetti a cui si
       intestano. */
    { percorso: '/soggetti', Componente: SoggettiFatturazione, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'amministrazione', etichetta: 'Aziende', Icona: Building2, menu: true, vaiA: true,
      cerca: ['Soggetti di fatturazione'] },
    { percorso: '/sistema-ts', Componente: SistemaTS, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'amministrazione', etichetta: 'Tessera Sanitaria', Icona: Landmark, menu: true, vaiA: true,
      cerca: ['Sistema TS', 'Sistema Tessera Sanitaria'] },
    { percorso: '/spettanze', Componente: Spettanze, guardia: 'protetta',
      gruppo: 'amministrazione', etichetta: 'Spettanze', Icona: Wallet, menu: true, vaiA: true,
      cerca: ['Compensi'] },
    /* Il conto prodotti del medico. La linguetta dei Conti medici sta
       dentro il Magazzino, che ai medici e' chiuso: senza questa rotta il
       permesso «ciascuno vede il proprio conto» non avrebbe nessuna porta.
       Etichetta scritta, non un'icona da scoprire cliccandoci sopra. */
    { percorso: '/mio-conto-prodotti', Componente: MioContoProdotti, guardia: 'ruoli', ruoli: MEDICI,
      gruppo: 'amministrazione', etichetta: 'Il mio conto prodotti', Icona: Boxes, menu: true, vaiA: true },
    { percorso: '/magazzino', Componente: Magazzino, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'amministrazione', etichetta: 'Magazzino', Icona: Package, menu: true, vaiA: true,
      azioni: [{ codice: 'nuovo-prodotto', etichetta: 'Nuovo prodotto' }] },
    /* «Prestazioni» diventa «Catalogo» (stile Console); il catalogo resta
       aperto a tutti. */
    { percorso: '/prestazioni', Componente: PrestazioniList, guardia: 'protetta',
      gruppo: 'amministrazione', etichetta: 'Catalogo', Icona: Syringe, menu: true, vaiA: true,
      linguetta: 'Catalogo', cerca: ['Prestazioni', 'Listino'] },
    /* «Voci con scadenza» (Alex, 25/09/2026): era «Pacchetti speciali», e
       chi cercava dove si vende un pacchetto a sedute apriva questa. Il
       percorso resta: i segnalibri non si rompono. Dal 2/10/2026 e' una
       linguetta del Catalogo. Si guardano, non si fatturano: la schermata e'
       del front-desk come le altre voci di cassa. */
    { percorso: '/pacchetti-speciali', Componente: PacchettiSpeciali, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'amministrazione', etichetta: 'Voci con scadenza', Icona: PackageOpen, vaiA: true,
      linguettaDi: '/prestazioni', linguetta: 'Voci con scadenza', cerca: ['Pacchetti speciali'] },
    /* Medici in Amministrazione (Alex, 2/10/2026). */
    { percorso: '/medici', Componente: MediciList, guardia: 'protetta',
      gruppo: 'amministrazione', etichetta: 'Medici', Icona: Stethoscope, menu: true, vaiA: true,
      cerca: ['Professionisti'] },
    { percorso: '/controllo-gestione', Componente: ControlloGestione, guardia: 'admin',
      gruppo: 'amministrazione', etichetta: 'Controllo di gestione', Icona: LineChart, menu: true, vaiA: true },
    /* Solo amministrazione, come il controllo di gestione: il margine per
       specialita' dice quanto la struttura guadagna su ogni branca, e non e'
       informazione da front-desk. Il controllo vero sta sul server. */
    { percorso: '/analisi', Componente: PannelloAnalisi, guardia: 'admin',
      gruppo: 'amministrazione', etichetta: 'Analisi dei dati', Icona: PieChart, menu: true, vaiA: true },

    /* ------------------------------------------------------------------
       Sistema
       ------------------------------------------------------------------ */
    /* «Controlli» porta allo stato del sistema (Alex, 2/10/2026): backup,
       lavori notturni, scadenze, allarmi. SOLO ADMIN, e lo fa rispettare
       anche il server: questa pagina dice dove stanno i backup e quando
       scadono i certificati. E' la prima cosa che si apre dopo un allarme. */
    { percorso: '/stato-sistema', Componente: StatoSistema, guardia: 'admin',
      gruppo: 'sistema', etichetta: 'Controlli', Icona: Activity, menu: true, vaiA: true,
      cerca: ['Stato del sistema', 'Backup', 'Allarmi'] },
    /* Utenti compare anche alla segreteria: e' lei che apre l'accesso a una
       collega nuova il primo giorno. Vede l'elenco e crea utenti (non
       amministratori); password e sospensioni restano all'admin. */
    { percorso: '/utenti', Componente: GestioneUtenti, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'sistema', etichetta: 'Utenti', Icona: UserCog, menu: true, vaiA: true,
      cerca: ['Gestione utenti', 'Account'],
      azioni: [{ codice: 'nuovo-utente', etichetta: 'Nuovo utente' }] },
    { percorso: '/registro-accessi', Componente: RegistroAccessi, guardia: 'admin',
      gruppo: 'sistema', etichetta: 'Registro accessi', Icona: FileSearch, menu: true, vaiA: true,
      cerca: ['Accessi'] },
    { percorso: '/registro-modifiche', Componente: RegistroModifiche, guardia: 'admin',
      gruppo: 'sistema', etichetta: 'Registro modifiche', Icona: History, menu: true, vaiA: true,
      cerca: ['Modifiche'] },
    /* CHI HA APERTO LE PROCEDURE RISERVATE. Nel menu per tutti i ruoli che
       la rotta ammette: il medico ci trova le aperture sulle PROPRIE
       procedure - e' lui la persona a cui quella traccia serve - e
       l'amministrazione le vede tutte. Chi filtra e' il server. Una voce
       nascosta a un ruolo che il server autorizza e' una funzione che quel
       ruolo non sa di avere, e questa e' la funzione su cui si regge tutto
       il controllo della riservatezza. */
    { percorso: '/aperture-riservate', Componente: ApertureRiservate, guardia: 'protetta',
      gruppo: 'sistema', etichetta: 'Aperture riservate', Icona: ShieldAlert, menu: true, vaiA: true },
    { percorso: '/cestino', Componente: Cestino, guardia: 'admin',
      gruppo: 'sistema', etichetta: 'Cestino', Icona: Trash2, menu: true, vaiA: true },
    { percorso: '/orari', Componente: GestioneOrari, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'sistema', etichetta: 'Orari', Icona: CalendarClock, menu: true, vaiA: true },
    { percorso: '/stanze', Componente: GestioneStanze, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'sistema', etichetta: 'Stanze', Icona: DoorOpen, menu: true, vaiA: true,
      cerca: ['Apparecchiature'] },
    /* «Tablet»: dispositivi (creazione e chiave solo admin) e compilazioni
       in sospeso (anche segreteria, che sta al banco quando un tablet si
       ferma). */
    { percorso: '/dispositivi', Componente: Dispositivi, guardia: 'ruoli', ruoli: FRONT_DESK,
      gruppo: 'sistema', etichetta: 'Tablet', Icona: IconaTablet, menu: true, vaiA: true,
      cerca: ['Dispositivi'] },
    /* Le sedi degli albi le aggiunge solo l'amministrazione. */
    { percorso: '/albi', Componente: AlbiSedi, guardia: 'admin',
      gruppo: 'sistema', etichetta: 'Albi e sedi', Icona: BadgeCheck, menu: true, vaiA: true },
    /* I dati della societa' per la fattura elettronica (blocco 6): solo
       admin, come la rotta del server. */
    { percorso: '/dati-fiscali', Componente: DatiFiscali, guardia: 'admin',
      gruppo: 'sistema', etichetta: 'Dati fiscali', Icona: Scale, menu: true, vaiA: true },
    /* Quanto costa l'assistente, non come si usa: «Assistenza» si chiamava
       come il pulsante fluttuante che risponde sui manuali, ed e' un'altra
       cosa. */
    { percorso: '/assistenza-admin', Componente: AmministrazioneAi, guardia: 'admin',
      gruppo: 'sistema', etichetta: 'Consumi AI', Icona: Sparkles, menu: true, vaiA: true,
      cerca: ['Consumi dell\'assistente AI', 'Assistente AI'] },
    /* La pagina Amministrazione si scioglie nei gruppi (Alex, 2/10/2026): i
       suoi riquadri sono voci del menu. La rotta resta per i segnalibri, ma
       nessuno la offre. */
    { percorso: '/amministrazione', Componente: Amministrazione, guardia: 'admin',
      gruppo: 'sistema', etichetta: 'Amministrazione', Icona: Settings },

    /* Il profilo lo raggiungono tutti i ruoli: e' anche l'unica schermata
       aperta a chi e' obbligato al secondo fattore e non l'ha ancora
       attivato. Non e' una voce del menu: ci si arriva dall'utente. */
    { percorso: '/profilo', Componente: Profilo, guardia: 'protetta',
      gruppo: null, etichetta: 'Il mio account', Icona: UserRound, vaiA: true,
      cerca: ['Profilo', 'Password', 'Verifica in due passaggi'] },

    /* ------------------------------------------------------------------
       Rimandi
       ------------------------------------------------------------------ */
    /* La pagina iniziale dipende dallo stile dell'utenza (Alex, 14/10/2026):
       con lo stile di prima e' la Dashboard del 638a8209, con lo stile nuovo
       «Oggi», e /dashboard ci rimanda (Alex, 2/10/2026, domanda 7: i
       segnalibri della clinica e le prove che la aprivano continuano a
       funzionare). La Dashboard non e' una voce di questa tabella per il
       menu nuovo: nel menu di prima la scrive LayoutClassico. */
    { percorso: '/dashboard', Componente: Dashboard, guardia: 'protetta', stile: 'classico',
      gruppo: 'lavoro', etichetta: 'Dashboard' },
    { percorso: '/', guardia: 'rimando', rimanda: paginaIniziale },
    { percorso: '*', guardia: 'rimando', rimanda: paginaIniziale },
];

const GUARDIE_NEL_GUSCIO = ['protetta', 'admin', 'ruoli'];

/** Lo stile dell'utente, coi nomi della colonna `stile`. */
export const stileDi = (utente) => (stileNuovo(utente) ? 'console' : 'classico');

/* Il guscio di prima, per App.js (vedi l'import qui sopra). */
export { LayoutClassico };

/** La schermata sta nel guscio (menu e barra in alto)? */
export const nelGuscio = (s) => GUARDIE_NEL_GUSCIO.includes(s.guardia);

/**
 * Che cosa fa la rotta di una schermata per questo utente:
 *   { rendi: true }  la mostra;
 *   { vai: '...' }   rimanda li' ('/login' porta con se' la provenienza).
 * E' la guardia di App.js, e il menu e Ctrl K chiedono a lei: per questo
 * non possono offrire una schermata che la rotta non apre.
 */
export function esitoGuardia(s, utente, autenticato) {
    switch (s.guardia) {
        case 'pubblica':
            return { rendi: true };
        case 'rimando':
            return { vai: typeof s.rimanda === 'function' ? s.rimanda(autenticato ? utente : null) : s.rimanda };
        case 'cambioPassword':
            if (!autenticato) return { vai: '/login' };
            if (!utente?.deve_cambiare_password) return { vai: paginaIniziale(utente) };
            return { rendi: true };
        case 'acquisizione':
            if (!autenticato) return { vai: '/login' };
            if (!RUOLI_ACQUISIZIONE.includes(utente?.ruolo)) return { vai: paginaIniziale(utente) };
            return { rendi: true };
        case 'protetta':
        case 'admin':
        case 'ruoli': {
            if (!autenticato) return { vai: '/login' };
            if (utente?.deve_cambiare_password) return { vai: '/cambia-password' };
            /* Il telefono dello studio ha una pagina sola: da qualunque
               rotta protetta torna li'. Non e' protezione (il server rifiuta
               ogni altra rotta con 403): serve a non mostrare una schermata
               piena di errori. */
            if (utente?.ruolo === 'acquisizione') return { vai: '/acquisizione' };
            /* Chi non ha il ruolo torna alla pagina iniziale. Nascondere la
               voce non basta: un medico che digitava l'indirizzo ci arrivava,
               e vedeva una pagina vuota o un errore tecnico. */
            const ruoli = s.guardia === 'admin' ? ['admin'] : (s.guardia === 'ruoli' ? s.ruoli : null);
            if (ruoli && !ruoli.includes(utente?.ruolo)) return { vai: paginaIniziale(utente) };
            /* Una schermata di uno stile solo: con l'altro, la pagina
               iniziale dell'utente (interruttore dello stile). */
            if (s.stile && s.stile !== stileDi(utente)) return { vai: paginaIniziale(utente) };
            return { rendi: true };
        }
        default:
            throw new Error(`schermate.js: guardia sconosciuta «${s.guardia}» su ${s.percorso}`);
    }
}

/** Si apre nel gestionale, per questo utente collegato? */
export const siApre = (s, utente) =>
    nelGuscio(s) && !!utente && esitoGuardia(s, utente, true).rendi === true;

const perPercorso = new Map(SCHERMATE.map((s) => [s.percorso, s]));

/** La riga della tabella con questo percorso (quello dichiarato, «:id» compreso). */
export const schermataDi = (percorso) => perPercorso.get(percorso) || null;

/** Il menu dell'utente: i gruppi non vuoti, nell'ordine di GRUPPI. */
export function vociMenu(utente) {
    return GRUPPI
        .map((g) => ({ ...g, voci: SCHERMATE.filter((s) => s.menu && s.gruppo === g.codice && siApre(s, utente)) }))
        .filter((g) => g.voci.length > 0);
}

/** Il «vai a» di Ctrl K: le schermate che si possono aprire per nome. */
export const vociVaiA = (utente) => SCHERMATE.filter((s) => s.vaiA && siApre(s, utente));

/** Le azioni di creazione dell'utente, per Ctrl K e per «Nuovo». */
export function azioniDi(utente) {
    const fuori = [];
    for (const s of SCHERMATE) {
        if (!s.azioni || !siApre(s, utente)) continue;
        for (const a of s.azioni) {
            if (a.ruoli && !a.ruoli.includes(utente?.ruolo)) continue;
            fuori.push({ codice: a.codice, etichetta: a.etichetta, percorso: s.percorso,
                         stato: { azione: a.codice }, gruppo: s.gruppo });
        }
    }
    return fuori;
}

/** La schermata madre di una famiglia di linguette (o la schermata stessa). */
const madreDi = (s) => (s?.linguettaDi ? schermataDi(s.linguettaDi) : s);

/**
 * Le linguette da mostrare sopra questa schermata: la madre e le sue
 * linguette che l'utente puo' aprire. Una sola vuol dire nessuna.
 */
export function linguetteDi(s, utente) {
    const madre = madreDi(s);
    if (!madre) return [];
    const famiglia = SCHERMATE.filter((x) => x === madre || x.linguettaDi === madre.percorso)
        .filter((x) => siApre(x, utente));
    return famiglia.length > 1 ? famiglia : [];
}

/** Il percorso della voce del menu che si accende su questa schermata. */
export function voceAccesa(s) {
    let x = s;
    for (let i = 0; x && i < 4; i += 1) {
        if (x.menu) return x.percorso;
        x = schermataDi(x.linguettaDi || x.genitore);
    }
    return null;
}

/**
 * Il percorso in alto: il gruppo, la schermata da cui si arriva (se c'e')
 * e la schermata. Solo etichette e percorsi dichiarati: mai un dato.
 *
 * La schermata da cui si arriva e' un collegamento SOLO se l'utente la
 * apre (siApre, come il menu, Ctrl K e le linguette): altrimenti resta
 * l'etichetta. Oggi ogni madre e ogni genitore sono aperti almeno quanto
 * le loro schermate, ma un genitore piu' stretto aggiunto domani
 * metterebbe in barra un clic che rimanda alla pagina iniziale. Lo controlla
 * scripts/verificaNavigazione.js, ruolo per ruolo.
 */
export function percorsoDi(s, utente) {
    if (!s || !nelGuscio(s)) return [];
    const fuori = [];
    const g = GRUPPI.find((x) => x.codice === s.gruppo);
    if (g) fuori.push({ etichetta: g.etichetta });
    const sopra = schermataDi(s.linguettaDi || s.genitore);
    if (sopra) fuori.push({ etichetta: sopra.etichetta, percorso: siApre(sopra, utente) ? sopra.percorso : null });
    fuori.push({ etichetta: s.etichetta, percorso: s.percorso.includes(':') ? null : s.percorso });
    return fuori;
}
